import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import toast, { Toaster } from "react-hot-toast";
import { getAvailableTimes } from "../api/calendlyApi";
import "./FormStyles.css";

const ContactForm = () => {
  const { t, i18n } = useTranslation();
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedDay, setSelectedDay] = useState("");
  const [loading, setLoading] = useState(true);
  const [availabilityError, setAvailabilityError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const webhookUrl = process.env.REACT_APP_CONTACT_WEBHOOK_URL;

  const initialFormState = {
    fullName: "",
    company: "",
    email: "",
    phone: "",
    service: "",
    purpose: "",
    selectedSlot: "",
    urgency: "",
    message: "",
  };

  const [formData, setFormData] = useState(initialFormState);

  const resetForm = () => {
    setFormData(initialFormState);
    setSelectedDay("");
  };

  const createValidationSchema = async () => {
    const Yup = await import("yup");

    return Yup.object().shape({
      fullName: Yup.string()
        .min(3, t('validation.name_short'))
        .required(t('validation.name_req')),
      email: Yup.string()
        .email(t('validation.email_invalid'))
        .matches(
          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          t('validation.email_en_only')
        )
        .required(t('validation.email_req')),
      phone: Yup.string()
        .matches(/^[0-9]+$/, t('validation.phone_num_only'))
        .length(8, t('validation.phone_len'))
        .required(t('validation.phone_req')),
      service: Yup.string().required(t('validation.service_req')),
      purpose: Yup.string().required(t('validation.purpose_req')),
      selectedSlot: Yup.string().required(t('validation.slot_req')),
      urgency: Yup.string().required(t('validation.urgency_req')),
      message: Yup.string().required(t('validation.message_req')),
    });
  };

  useEffect(() => {
    const getData = async () => {
      try {
        setLoading(true);
        setAvailabilityError("");
        const result = await getAvailableTimes();
        const slots = Array.isArray(result) ? result : result?.collection;
        setAvailableSlots(Array.isArray(slots) ? slots : []);

        if (!Array.isArray(result) && !result?.ok) {
          setAvailabilityError(t('messages.availability_error'));
        }
      } catch (error) {
        console.error("Error loading slots:", error);
        setAvailableSlots([]);
        setAvailabilityError(t('messages.availability_error'));
      } finally {
        setLoading(false);
      }
    };
    getData();
  }, [t]);

  useEffect(() => {
    setSelectedDay("");
    setFormData(prev => ({ ...prev, selectedSlot: "" }));
  }, [i18n.language]);

  const uniqueDays = useMemo(() => {
    const days = availableSlots.map(slot => new Date(slot.start_time).toDateString());
    return [...new Set(days)];
  }, [availableSlots]);

  const filteredTimes = useMemo(() => {
    if (!selectedDay) return [];
    return availableSlots.filter(
      (slot) => new Date(slot.start_time).toDateString() === selectedDay
    );
  }, [selectedDay, availableSlots]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    try {
      const validationSchema = await createValidationSchema();
      await validationSchema.validate(formData, { abortEarly: false });

      if (!webhookUrl) {
        toast.error(t('messages.webhook_missing'), {
          style: { background: "#0f172a", color: "#fff", border: "1px solid #ef4444" },
        });
        return;
      }

      setIsSubmitting(true);
      const loadingToast = toast.loading(t('messages.loading_toast'));

      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!response.ok) throw new Error("Network response was not ok");

      toast.dismiss(loadingToast);
      toast.success(t('messages.success'), {
        duration: 5000,
        style: { background: "#0f172a", color: "#fff", border: "1px solid #2563eb" },
      });

      resetForm();
    } catch (err) {
      setIsSubmitting(false);
      const validationErrors = {};

      if (err.inner) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message;
        });
        setErrors(validationErrors);
        toast.error(err.inner[0].message, {
          style: { background: "#0f172a", color: "#fff", border: "1px solid #ef4444" },
        });
      } else {
        toast.error(t('messages.error_unexpected'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDayDisplay = (dateStr) =>
    new Date(dateStr).toLocaleDateString(i18n.language === 'ar' ? "ar-KW" : "en-US", {
      weekday: "long", day: "numeric", month: "long",
    });

  const formatTimeDisplay = (dateStr) =>
    new Date(dateStr).toLocaleTimeString(i18n.language === 'ar' ? "ar-KW" : "en-US", {
      hour: "2-digit", minute: "2-digit", hour12: true,
    });

  return (
    <div className="form-container">
      <Toaster position="top-center" reverseOrder={false} />

      <h1 className="main-title">{t('form.main_title')}</h1>
      <p className="sub-title">{t('form.sub_title')}</p>

      <form onSubmit={handleSubmit}>
        <section className="form-section">
          <h2 className="section-title">{t('form.section_contact')}</h2>
          <div className="row">
            <div className="input-group">
              <label htmlFor="fullName">{t('labels.fullName')}</label>
              <input
                id="fullName"
                name="fullName" type="text" placeholder={t('labels.placeholder_name')}
                value={formData.fullName}
                onChange={handleChange} className={errors.fullName ? "input-error" : ""}
              />
            </div>
            <div className="input-group">
              <label htmlFor="company">{t('labels.company')}</label>
              <input
                id="company"
                name="company" type="text" placeholder={t('labels.placeholder_company')}
                value={formData.company}
                onChange={handleChange}
              />
            </div>
            <div className="input-group">
              <label htmlFor="email">{t('labels.email')}</label>
              <input
                id="email"
                name="email" type="email" placeholder="email@example.com"
                value={formData.email}
                onChange={handleChange} className={errors.email ? "input-error" : ""}
                style={{ direction: 'ltr', textAlign: i18n.language === 'ar' ? 'right' : 'left' }}
              />
            </div>
          </div>
          <div className="row short-row">
            <div className="input-group">
              <label htmlFor="phone">{t('labels.phone')}</label>
              <input
                id="phone"
                name="phone" type="tel" placeholder="9XXXXXXX"
                value={formData.phone}
                onChange={handleChange} className={errors.phone ? "input-error" : ""}
              />
            </div>
          </div>
        </section>

        <section className="form-section">
          <h2 className="section-title">{t('form.section_interest')}</h2>
          <div className="row two-cols">
            <div className="input-group">
              <label htmlFor="service">{t('labels.service')}</label>
              <select id="service" name="service" value={formData.service} onChange={handleChange} className={errors.service ? "input-error" : ""}>
                <option value="" disabled>{t('labels.select_default')}</option>
                <option value="web-design">{t('options.services.web')}</option>
                <option value="mobile-apps">{t('options.services.apps')}</option>
                <option value="e-commerce">{t('options.services.ecommerce')}</option>
                <option value="erp-systems">{t('options.services.erp')}</option>
                <option value="ai-solutions">{t('options.services.ai')}</option>
              </select>
            </div>

            <div className="input-group">
              <label htmlFor="purpose">{t('labels.purpose')}</label>
              <select id="purpose" name="purpose" value={formData.purpose} onChange={handleChange} className={errors.purpose ? "input-error" : ""}>
                <option value="" disabled>{t('labels.purpose_default')}</option>
                <option value="book-appointment">{t('options.purposes.appointment')}</option>
                <option value="tech-consultation">{t('options.purposes.consultation')}</option>
                <option value="pricing-details">{t('options.purposes.pricing')}</option>
              </select>
            </div>
          </div>
        </section>

        <section className="form-section" id="services">
          <h2 className="section-title">{t('form.section_time')}</h2>
          {availabilityError && <p className="api-error">{availabilityError}</p>}
          <div className="row three-cols">
            <div className="input-group">
              <label htmlFor="selectedDay">{t('labels.day')}</label>
              <select
                id="selectedDay"
                value={selectedDay}
                onChange={(e) => {
                  setSelectedDay(e.target.value);
                  setFormData(prev => ({ ...prev, selectedSlot: "" }));
                }}
                disabled={loading || uniqueDays.length === 0}
              >
                <option value="" disabled>
                  {loading ? t('form.loading') : uniqueDays.length === 0 ? t('form.no_days') : t('form.select_day')}
                </option>
                {uniqueDays.map((dayString) => (
                  <option key={dayString} value={dayString}>
                    {formatDayDisplay(dayString)}
                  </option>
                ))}
              </select>
            </div>

            <div className="input-group">
              <label htmlFor="selectedSlot">{t('labels.time')}</label>
              <select
                id="selectedSlot"
                name="selectedSlot"
                value={formData.selectedSlot}
                disabled={!selectedDay}
                onChange={handleChange}
                className={errors.selectedSlot ? "input-error" : ""}
              >
                <option value="" disabled>{selectedDay ? t('form.select_time') : t('form.select_day_first')}</option>
                {filteredTimes.map((slot) => (
                  <option key={slot.start_time} value={slot.start_time}>{formatTimeDisplay(slot.start_time)}</option>
                ))}
              </select>
            </div>

            <div className="input-group">
              <label htmlFor="urgency">{t('labels.urgency')}</label>
              <select id="urgency" name="urgency" value={formData.urgency} onChange={handleChange} className={errors.urgency ? "input-error" : ""}>
                <option value="" disabled>{t('labels.urgency_default')}</option>
                <option value="low">{t('options.urgency_levels.low')}</option>
                <option value="medium">{t('options.urgency_levels.medium')}</option>
                <option value="high">{t('options.urgency_levels.high')}</option>
                <option value="immediate">{t('options.urgency_levels.immediate')}</option>
              </select>
            </div>
          </div>
        </section>

        <section className="form-section">
          <h2 className="section-title">{t('form.section_additional')}</h2>
          <div className="input-group full-width">
            <label htmlFor="message">{t('labels.message')}</label>
            <textarea
              id="message"
              name="message"
              placeholder={t('labels.placeholder_message')}
              value={formData.message}
              onChange={handleChange}
              className={errors.message ? "input-error" : ""}
            ></textarea>
          </div>
        </section>

        <button type="submit" className="submit-btn" disabled={isSubmitting}>
          {isSubmitting ? t('form.sending') : t('form.submit_btn')}
        </button>
      </form>
    </div>
  );
};

export default ContactForm;
